import React, { useEffect, useState } from "react";
import {
  CLASSIC_NOVEL_FUNDAMENTAL_KEYS,
  normalizeComboMembers,
  shouldFlipComboMember,
} from "../indicators/comboConfig";
import {
  Z_SCORE_BASE_LABELS,
  formatZScoreYears,
  getZScoreYearsForIndicator,
} from "../indicators/zScoreConfig";

const BRAND = {
  bg: "#0a0a0f",
  panel: "#1a1a24",
  border: "#2f2f34",
  borderLight: "#3a3a4a",
  text: "#e8e8ec",
  muted: "#9ca3af",
  accent: "#6366f1",
};

const btn = {
  background: BRAND.panel,
  color: BRAND.text,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
};

const btnPrimary = {
  ...btn,
  background: BRAND.accent,
  border: `1px solid ${BRAND.accent}`,
  color: "#fff",
};

function CompactCheck({ checked, onChange, children, style }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        color: BRAND.text,
        ...style,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0, margin: 0 }}
      />
      <span
        aria-hidden
        style={{
          width: 14,
          height: 14,
          boxSizing: "border-box",
          flexShrink: 0,
          borderRadius: 3,
          border: `1.5px solid ${checked ? BRAND.accent : BRAND.borderLight}`,
          background: checked ? BRAND.accent : "transparent",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2 5.2L4.1 7.2L8 2.8"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      {children}
    </label>
  );
}

export default function ComboMembersModal({
  open,
  onClose,
  members,
  zScoreYearsMap,
  onConfirm,
}) {
  const [selected, setSelected] = useState(() => normalizeComboMembers(members));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelected(normalizeComboMembers(members));
    setError("");
  }, [open, members]);

  if (!open) return null;

  const toggle = (key) => {
    setError("");
    setSelected((prev) => {
      const set = new Set(prev);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return CLASSIC_NOVEL_FUNDAMENTAL_KEYS.filter((k) => set.has(k));
    });
  };

  const selectAll = () => {
    setError("");
    setSelected([...CLASSIC_NOVEL_FUNDAMENTAL_KEYS]);
  };

  const clearAll = () => {
    setError("");
    setSelected([]);
  };

  const handleConfirm = () => {
    if (!selected.length) {
      setError("Select at least one indicator to combine.");
      return;
    }
    onConfirm(normalizeComboMembers(selected));
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 12000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 520,
          maxWidth: "94vw",
          maxHeight: "90vh",
          overflow: "auto",
          background: BRAND.panel,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 16,
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: BRAND.text }}>
          Combine Classic / Novel Fundamental
        </div>
        <div style={{ fontSize: 14, color: BRAND.muted, marginBottom: 16, lineHeight: 1.5 }}>
          Choose which Z-score indicators to include. Each selected series gets{" "}
          <strong style={{ color: BRAND.text }}>equal weight</strong> (simple average).
          For most members, Z &lt; −1 is cheap. For{" "}
          <strong style={{ color: BRAND.text }}>EV/EBITDA yield</strong> and{" "}
          <strong style={{ color: BRAND.text }}>Dividend yield</strong>, Z &gt; +1 is cheap —
          those are <strong style={{ color: BRAND.text }}>inverted</strong> in the combo so
          low still means cheaper across the board.
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button type="button" style={btn} onClick={selectAll}>
            Select all
          </button>
          <button type="button" style={btn} onClick={clearAll}>
            Clear
          </button>
        </div>

        <div
          style={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: 8,
            padding: 8,
            marginBottom: error ? 8 : 16,
          }}
        >
          {CLASSIC_NOVEL_FUNDAMENTAL_KEYS.map((key) => {
            const years = getZScoreYearsForIndicator(key, zScoreYearsMap);
            const base = Z_SCORE_BASE_LABELS[key] || key;
            const inverted = shouldFlipComboMember(key);
            return (
              <CompactCheck
                key={key}
                checked={selected.includes(key)}
                onChange={() => toggle(key)}
                style={{ padding: "6px 4px" }}
              >
                <span>
                  {base}{" "}
                  <span style={{ color: BRAND.muted, fontSize: 12 }}>
                    (Z vs {formatZScoreYears(years)})
                  </span>
                  {inverted ? (
                    <span style={{ color: BRAND.accent, fontSize: 12, marginLeft: 6 }}>
                      · inverted (Z&gt;1 = buy)
                    </span>
                  ) : (
                    <span style={{ color: BRAND.muted, fontSize: 12, marginLeft: 6 }}>
                      · Z&lt;−1 = buy
                    </span>
                  )}
                </span>
              </CompactCheck>
            );
          })}
        </div>

        {error ? (
          <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : (
          <div style={{ fontSize: 13, color: BRAND.muted, marginBottom: 16 }}>
            {selected.length} selected · weight each ={" "}
            {selected.length ? `${(100 / selected.length).toFixed(1)}%` : "—"}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" style={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" style={btnPrimary} onClick={handleConfirm}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
